<template>
  <div v-if="cfg" class="funnel-landing bg-white">

    <!-- ── Hero ─────────────────────────────────────────── -->
    <section class="bg-[#111] text-white overflow-hidden">
      <div class="max-w-screen-xl mx-auto px-6 py-14 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <p class="text-[0.875rem] font-semibold text-[#FFD500] mb-4 uppercase tracking-[0.03em]">
            <span aria-hidden="true">⚡</span> {{ cfg.eyebrow }}
          </p>
          <h1 class="text-white text-[clamp(2rem,5vw,3.5rem)] font-black leading-[1.08] mb-5">{{ cfg.title }}</h1>
          <p class="text-[1.0625rem] sm:text-[1.1875rem] leading-[1.7] text-[#ccc] mb-8 max-w-[540px]">
            {{ cfg.sub }}
          </p>
          <div class="flex flex-col sm:flex-row gap-4">
            <router-link :to="cfg.categoryLink" class="btn-primary">
              <i class="bi bi-lightning-charge"></i> {{ cfg.ctaLabel }}
            </router-link>
            <a href="#advies" class="btn-ghost">
              <i class="bi bi-chat-dots"></i> Gratis advies
            </a>
          </div>

          <router-link :to="`/bundel/${$route.params.slug}`" class="inline-flex items-center gap-2 mt-5 text-[#FFD500] font-semibold no-underline hover:gap-3 transition-all">
            Of stel je complete bundel samen <i class="bi bi-arrow-right"></i>
          </router-link>

          <!-- Trust strip -->
          <ul class="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 list-none p-0 m-0 text-[0.875rem] text-[#aaa]">
            <li class="flex items-center gap-2"><i class="bi bi-truck text-[#FFD500]"></i> Gratis verzending v.a. €50</li>
            <li class="flex items-center gap-2"><i class="bi bi-shield-check text-[#FFD500]"></i> 2 jaar garantie</li>
            <li class="flex items-center gap-2"><i class="bi bi-arrow-repeat text-[#FFD500]"></i> 30 dagen retour</li>
          </ul>
        </div>

        <div class="relative">
          <img :src="cfg.heroImage" :alt="cfg.heroAlt" class="rounded-[20px] w-full h-auto object-cover aspect-[4/3] shadow-[0_20px_50px_rgba(0,0,0,0.4)]" />
          <div class="absolute -bottom-4 -left-4 bg-white rounded-xl px-5 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.2)] flex items-center gap-3">
            <span class="text-[1.75rem] leading-none">💰</span>
            <div>
              <strong class="block text-[#111] text-[0.9375rem] font-bold">{{ cfg.badge.title }}</strong>
              <small class="block text-[#444] text-[0.75rem]">{{ cfg.badge.sub }}</small>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Voordelen ────────────────────────────────────── -->
    <section class="max-w-screen-xl mx-auto px-6 py-16 lg:py-20">
      <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-[#111] text-center mb-12">
        Waarom {{ cfg.shortName }}?
      </h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div v-for="(u, i) in cfg.usps" :key="i" class="bg-[#fafafa] rounded-[14px] p-6 border border-[#eee]">
          <div class="w-12 h-12 rounded-[10px] bg-[#fffbe6] flex items-center justify-center text-[#FFD500] text-xl mb-4">
            <i :class="['bi', u.icon]"></i>
          </div>
          <strong class="block text-[1.0625rem] text-[#111] mb-2">{{ u.title }}</strong>
          <p class="text-[0.9rem] text-[#666] leading-[1.6] m-0">{{ u.text }}</p>
        </div>
      </div>
    </section>

    <!-- ── Cijfers ──────────────────────────────────────── -->
    <section class="bg-[#111] text-white py-14">
      <div class="max-w-screen-xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
        <div v-for="(s, i) in cfg.stats" :key="i">
          <strong class="block text-[clamp(1.75rem,4vw,2.75rem)] font-black text-[#FFD500] leading-none mb-2">{{ s.value }}</strong>
          <span class="block text-[0.9375rem] text-[#bbb]">{{ s.label }}</span>
        </div>
      </div>
    </section>

    <!-- ── Zo werkt het ─────────────────────────────────── -->
    <section class="max-w-screen-xl mx-auto px-6 py-16 lg:py-20">
      <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-[#111] text-center mb-12">In 3 stappen klaar</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div v-for="(step, i) in cfg.steps" :key="i" class="relative">
          <span class="text-[3rem] font-black text-[#FFD500]/25 leading-none">0{{ i + 1 }}</span>
          <strong class="block text-[1.125rem] text-[#111] mt-2 mb-2">{{ step.title }}</strong>
          <p class="text-[0.9375rem] text-[#666] leading-[1.6] m-0">{{ step.text }}</p>
        </div>
      </div>
    </section>

    <!-- ── Social proof ─────────────────────────────────── -->
    <section class="bg-[#fafafa] py-16 lg:py-20 border-y border-[#eee]">
      <div class="max-w-screen-xl mx-auto px-6">
        <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-[#111] text-center mb-12">Wat klanten zeggen</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <figure v-for="(r, i) in cfg.reviews" :key="i" class="bg-white rounded-[14px] p-6 border border-[#eee] m-0">
            <div class="text-[#FFD500] mb-3" aria-hidden="true">★★★★★</div>
            <blockquote class="text-[0.95rem] text-[#333] leading-[1.65] m-0 mb-4">"{{ r.text }}"</blockquote>
            <figcaption class="text-[0.85rem] text-[#888] font-semibold">- {{ r.author }}</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <!-- ── Lead-form ────────────────────────────────────── -->
    <div id="advies">
      <NewsletterSubscribe />
    </div>

    <!-- ── FAQ ──────────────────────────────────────────── -->
    <section class="max-w-3xl mx-auto px-6 py-16 lg:py-20">
      <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-[#111] text-center mb-10">Veelgestelde vragen</h2>
      <div class="flex flex-col gap-3">
        <details v-for="(f, i) in cfg.faq" :key="i" class="group bg-[#fafafa] rounded-[12px] border border-[#eee] px-5 py-4">
          <summary class="cursor-pointer list-none flex items-center justify-between font-semibold text-[#111] text-[1rem]">
            {{ f.q }}
            <i class="bi bi-plus-lg text-[#FFD500] transition-transform group-open:rotate-45"></i>
          </summary>
          <p class="text-[0.9375rem] text-[#666] leading-[1.7] mt-3 mb-0">{{ f.a }}</p>
        </details>
      </div>
    </section>

    <!-- ── Slot-CTA ─────────────────────────────────────── -->
    <section class="bg-[#FFD500] py-14">
      <div class="max-w-screen-xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
        <div>
          <h2 class="text-[clamp(1.5rem,3vw,2rem)] font-black text-[#111] mb-1">{{ cfg.finalCta.title }}</h2>
          <p class="text-[#333] text-[1rem] m-0">{{ cfg.finalCta.sub }}</p>
        </div>
        <router-link :to="cfg.categoryLink" class="shrink-0 bg-[#111] text-white font-bold text-[1.0625rem] px-8 py-4 rounded-[8px] no-underline inline-flex items-center gap-2 hover:bg-[#333] hover:-translate-y-[2px] transition-all">
          {{ cfg.ctaLabel }} <i class="bi bi-arrow-right"></i>
        </router-link>
      </div>
    </section>

  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import NewsletterSubscribe from '@components/NewsletterSubscribe.vue'

const route = useRoute()

const FUNNELS = {
  'balkon-zonnepanelen': {
    eyebrow: 'Balkon zonnepanelen',
    shortName: 'balkon zonnepanelen',
    title: 'Wek je eigen stroom op - vanaf je balkon',
    sub: 'Plug & play zonnepanelen zonder vergunning. Ophangen, inpluggen en direct besparen - óók als je huurt.',
    heroImage: '/wp-content/uploads/2026/07/balkonpanelen_met_batterij.png',
    heroAlt: 'Balkon zonnepanelen met thuisbatterij',
    ctaLabel: 'Bekijk balkonsets',
    categoryLink: '/categorie/balkon-zonnepanelen',
    badge: { title: 'Tot 40% besparing', sub: 'Op je energierekening' },
    usps: [
      { icon: 'bi-file-earmark-check', title: 'Geen vergunning', text: 'Balkonsystemen tot 800 W zijn in de meeste gemeenten vergunningsvrij.' },
      { icon: 'bi-plug', title: 'Plug & play', text: 'Ophangen en in het stopcontact - klaar in minder dan 30 minuten.' },
      { icon: 'bi-piggy-bank', title: 'Snel terugverdiend', text: 'Terugverdientijd van 2 tot 4 jaar, afhankelijk van je zonligging.' },
      { icon: 'bi-house-heart', title: 'Ook voor huurders', text: 'Geen bouwkundige ingrepen, dus je neemt het gewoon mee bij verhuizing.' },
    ],
    stats: [
      { value: '300–380', label: 'kWh opbrengst per jaar (400 Wp, zuid)' },
      { value: '€85–106', label: 'Besparing per jaar' },
      { value: '2–3 jaar', label: 'Gemiddelde terugverdientijd' },
    ],
    steps: [
      { title: 'Kies je set', text: 'Complete balkonset of losse panelen - wij helpen je kiezen wat past.' },
      { title: 'Hang op & plug in', text: 'Bevestig aan je balkonhek en steek de stekker in het stopcontact.' },
      { title: 'Bespaar direct', text: 'De opgewekte stroom verlaagt meteen je verbruik uit het net.' },
    ],
    reviews: [
      { text: 'Binnen een half uur hing alles. Ik zie het verschil nu al op de meter.', author: 'Mariska, Utrecht' },
      { text: 'Perfect voor onze huurwoning. Geen gedoe met de verhuurder.', author: 'Sem, Rotterdam' },
      { text: 'Goed advies gekregen over welke set bij mijn balkon paste. Top service.', author: 'Anoniem' },
    ],
    faq: [
      { q: 'Heb ik een vergunning nodig?', a: 'In de meeste gemeenten zijn balkonsystemen tot 800 W vergunningsvrij. Check altijd even de regels van je eigen gemeente.' },
      { q: 'Werkt dit ook op een noord-balkon?', a: 'Zuid is ideaal, maar oost- en westgericht leveren ook een zinvolle opbrengst. Vermijd vooral langdurige schaduw.' },
      { q: 'Kan ik het zelf installeren?', a: 'Ja. Een complete set is volledig plug & play en heeft geen installateur nodig.' },
    ],
    finalCta: { title: 'Klaar om te besparen?', sub: 'Bekijk onze balkonsets en start vandaag nog.' },
  },

  'thuisbatterij': {
    eyebrow: 'Thuisbatterijen',
    shortName: 'een thuisbatterij',
    title: 'Sla je zonne-energie op en gebruik ’m ’s avonds',
    sub: 'Bewaar overdag opgewekte stroom en verbruik ’m wanneer jij wilt. Minder afhankelijk van het net, lagere energiekosten.',
    heroImage: 'https://nl.jackery.com/cdn/shop/files/KV4_S3_Pro_Max_AC_NL_PC.jpg?v=1774266671&width=800',
    heroAlt: 'Jackery thuisbatterij energieopslag',
    ctaLabel: 'Bekijk thuisbatterijen',
    categoryLink: '/categorie/thuisbatterijen',
    badge: { title: 'Tot 90% zelfvoorzienend', sub: 'Met zonnepanelen' },
    usps: [
      { icon: 'bi-sun', title: 'Zonne-energie opslaan', text: 'Gebruik overdag opgewekte stroom ook ’s avonds en ’s nachts.' },
      { icon: 'bi-graph-down-arrow', title: 'Lagere energiekosten', text: 'Laad op het goedkoopste moment en verbruik op het duurste.' },
      { icon: 'bi-shield-check', title: 'Onafhankelijkheid', text: 'Minder afhankelijk van het net en van schommelende tarieven.' },
      { icon: 'bi-boxes', title: 'Hybride & modulair', text: 'Breid je opslag later eenvoudig uit wanneer je verbruik groeit.' },
    ],
    stats: [
      { value: 'tot 90%', label: 'Zelfvoorzienend met zon + opslag' },
      { value: '5–20 kWh', label: 'Beschikbare opslagcapaciteit' },
      { value: 'tot 10 jaar', label: 'Garantie op het systeem' },
    ],
    steps: [
      { title: 'Bepaal je capaciteit', text: 'Wij helpen je de juiste kWh kiezen op basis van je verbruik.' },
      { title: 'Koppel aan je opwek', text: 'Sluit de batterij aan op je zonnepanelen of dynamische tarief.' },
      { title: 'Verbruik slim', text: 'Gebruik je eigen stroom op het moment dat het ’t meest oplevert.' },
    ],
    reviews: [
      { text: 'Avonds draai ik nu op eigen zonnestroom. Mijn netverbruik is gekelderd.', author: 'Peter, Eindhoven' },
      { text: 'Advies was helder en eerlijk, geen verkooppraatje. Fijne ervaring.', author: 'Anoniem' },
      { text: 'Modulair systeem - begonnen klein, later uitgebreid. Werkt perfect.', author: 'Yasmin, Den Haag' },
    ],
    faq: [
      { q: 'Heb ik zonnepanelen nodig voor een thuisbatterij?', a: 'Niet per se. Zonder panelen kun je laden op goedkope (dynamische) tarieven en verbruiken wanneer stroom duur is.' },
      { q: 'Hoeveel kWh heb ik nodig?', a: 'Dat hangt af van je verbruik en opwek. Onze specialisten rekenen het gratis voor je uit.' },
      { q: 'Hoe lang gaat een thuisbatterij mee?', a: 'De meeste systemen hebben tot 10 jaar garantie en een nog langere technische levensduur.' },
    ],
    finalCta: { title: 'Word energie-onafhankelijk', sub: 'Bekijk ons assortiment thuisbatterijen.' },
  },
}

const cfg = computed(() => FUNNELS[route.params.slug] || null)
</script>

<style scoped>
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
  padding: 0.9rem 2rem; border-radius: 8px; font-weight: 700; font-size: 1.0625rem;
  text-decoration: none; background: #FFD500; color: #111;
  transition: background 0.2s ease, transform 0.2s ease;
}
.btn-primary:hover { background: #ffb800; transform: translateY(-2px); }
.btn-ghost {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
  padding: 0.9rem 2rem; border-radius: 8px; font-weight: 700; font-size: 1.0625rem;
  text-decoration: none; background: rgba(255,255,255,0.1); color: #fff;
  border: 2px solid rgba(255,255,255,0.28); transition: background 0.2s ease;
}
.btn-ghost:hover { background: rgba(255,255,255,0.2); }
</style>
